import { Button, Grid, Typography, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Chip } from "@mui/material";
import { useEffect, useState } from "react";
import { collection, deleteDoc, doc, onSnapshot, writeBatch } from "firebase/firestore";
import { db } from "../../../firebase/firebase";
import ModalCrearEditarItem from "../Componentes/SubComponentes/ModalCrearEditarComida";
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ─── Tarjeta individual sortable ───────────────────────────────────────────
const SortableCard = ({ item, index, onEditar, onEliminar, onInsertarDespues, mostrarEditar }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
    };

    return (
        <Grid
            ref={setNodeRef}
            style={style}
            item
            size={{ xs: 12, sm: 6, md: 4 }}
            sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                border: "1px solid #ccc",
                borderRadius: 2,
                backgroundColor: "white",
                cursor: "default",
                touchAction: "none",
            }}
        >
            {/* Handle de arrastre + número de posición */}
            <Grid container justifyContent="space-between" alignItems="center" sx={{ width: '100%', mb: 1 }}>
                <Chip label={`#${index + 1}`} size="small" color="primary" variant="outlined" />
                <DragIndicatorIcon
                    {...listeners}
                    {...attributes}
                    sx={{
                        cursor: 'grab',
                        color: '#aaa',
                        '&:active': { cursor: 'grabbing' },
                        touchAction: 'none',
                    }}
                />
            </Grid>

            {item.url && (
                <img
                    src={item.url}
                    alt={item.nombre || "imagen"}
                    style={{
                        width: "100%",
                        height: 200,
                        objectFit: "cover",
                        borderRadius: 8,
                        marginBottom: 8,
                        pointerEvents: "none",
                    }}
                />
            )}

            <Grid container justifyContent={mostrarEditar ? "space-between" : "center"} sx={{ width: '100%', mt: 1 }} spacing={1}>
                {mostrarEditar && (
                    <Button onClick={() => onEditar(item.id)} variant="outlined" size="small" sx={{ width: '48%' }}>
                        Editar
                    </Button>
                )}
                <Button
                    onClick={() => onEliminar(item.id)}
                    variant="contained"
                    color="error"
                    size="small"
                    sx={{ width: mostrarEditar ? '48%' : '60%' }}
                >
                    Eliminar
                </Button>

                {/* Botón + insertar debajo */}
                <Button
                    onClick={() => onInsertarDespues(index)}
                    variant="outlined"
                    color="success"
                    size="small"
                    startIcon={<AddCircleOutlineIcon />}
                    sx={{ width: '100%', mt: 0.5 }}
                >
                    Insertar debajo
                </Button>
            </Grid>
        </Grid>
    );
};

// ─── Lista principal ────────────────────────────────────────────────────────
const Lista_Items_Dashboard = ({ coleccion, mostrarEditar = true }) => {
    const [imagenes, setImagenes] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [mensajeExito, setMensajeExito] = useState('');
    const [modalEditarOpen, setModalEditarOpen] = useState(false);
    const [idEditar, setIdEditar] = useState(null);
    const [dialogEliminarOpen, setDialogEliminarOpen] = useState(false);
    const [idEliminar, setIdEliminar] = useState(null);
    const [guardandoOrden, setGuardandoOrden] = useState(false);

    // ── Nuevo: insertar después de un índice ──
    const [modalInsertarOpen, setModalInsertarOpen] = useState(false);
    const [insertarDespuesDeIndex, setInsertarDespuesDeIndex] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
    );

    useEffect(() => {
        const unsubscribe = onSnapshot(collection(db, coleccion), (querySnapshot) => {
            const docs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            docs.sort((a, b) => {
                if (a.orden !== undefined && b.orden !== undefined) return a.orden - b.orden;
                if (a.numero !== undefined && b.numero !== undefined) return a.numero - b.numero;
                return 0;
            });
            setImagenes(docs);
            setCargando(false);
        });
        return () => unsubscribe();
    }, [coleccion]);

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = imagenes.findIndex(i => i.id === active.id);
        const newIndex = imagenes.findIndex(i => i.id === over.id);
        const nuevoOrden = arrayMove(imagenes, oldIndex, newIndex);

        setImagenes(nuevoOrden);
        setGuardandoOrden(true);
        try {
            const batch = writeBatch(db);
            nuevoOrden.forEach((item, index) => {
                batch.update(doc(db, coleccion, item.id), { orden: index });
            });
            await batch.commit();
            setMensajeExito("Orden guardado ✅");
            setOpenSnackbar(true);
        } catch (error) {
            console.error("Error al guardar el orden:", error);
        } finally {
            setGuardandoOrden(false);
        }
    };

    // Abre el modal de inserción y guarda el índice tras el cual se insertará
    const handleInsertarDespues = (index) => {
        setInsertarDespuesDeIndex(index);
        setModalInsertarOpen(true);
    };

    // Después de que el modal crea el doc, reordena todo en Firestore
    // para que el nuevo item quede en posición insertarDespuesDeIndex + 1
    const handleSuccessInsertar = async (mensaje) => {
        setModalInsertarOpen(false);

        // Pequeña espera para que el onSnapshot reciba el nuevo doc
        await new Promise(r => setTimeout(r, 600));

        // En este punto `imagenes` todavía no incluye el nuevo doc (el snapshot
        // lo añadirá al final). Re-leemos el estado actualizado vía callback.
        setImagenes(prev => {
            const lista = [...prev];
            // El nuevo item llega al final (orden más alto o sin orden)
            // Movemos el último elemento a la posición insertarDespuesDeIndex + 1
            const nuevoItem = lista[lista.length - 1];
            if (!nuevoItem) return lista;

            lista.splice(lista.length - 1, 1);                    // quita del final
            lista.splice(insertarDespuesDeIndex + 1, 0, nuevoItem); // inserta en posición

            // Persiste el nuevo orden en Firestore
            const batch = writeBatch(db);
            lista.forEach((item, index) => {
                batch.update(doc(db, coleccion, item.id), { orden: index });
            });
            batch.commit().catch(console.error);

            return lista;
        });

        setMensajeExito(mensaje || "Imagen insertada ✅");
        setOpenSnackbar(true);
    };

    const handleAbrirEditar = (id) => { setIdEditar(id); setModalEditarOpen(true); };
    const handleCerrarEditar = () => { setModalEditarOpen(false); setIdEditar(null); };
    const handleAbrirDialogEliminar = (id) => { setIdEliminar(id); setDialogEliminarOpen(true); };
    const handleCerrarDialogEliminar = () => { setDialogEliminarOpen(false); setIdEliminar(null); };

    const handleEliminar = async () => {
        try {
            await deleteDoc(doc(db, coleccion, idEliminar));
            setMensajeExito("Elemento eliminado con éxito ✅");
            setOpenSnackbar(true);
            handleCerrarDialogEliminar();
        } catch (error) {
            console.error("❌ Error al eliminar:", error);
        }
    };

    const handleSuccess = (mensaje) => { setMensajeExito(mensaje); setOpenSnackbar(true); };

    if (cargando) return <Typography sx={{ pl: 2, pt: 4 }}>Cargando...</Typography>;
    if (imagenes.length === 0) return <Typography sx={{ pl: 2, pt: 4 }}>No hay elementos.</Typography>;

    return (
        <>
            <Grid container size={{ xs: 12 }} justifyContent="center" sx={{ pt: 2, pb: 0 }}>
                <Typography variant="caption" color="text.secondary">
                    {guardandoOrden ? "Guardando orden..." : "Arrastra el ícono ≡ para cambiar el orden"}
                </Typography>
            </Grid>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={imagenes.map(i => i.id)} strategy={rectSortingStrategy}>
                    <Grid
                        container
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        size={{ xs: 12, sm: 12, md: 10 }}
                        sx={{ maxWidth: 1200, px: 3, pt: 2 }}
                        spacing={2}
                    >
                        {imagenes.map((item, index) => (
                            <SortableCard
                                key={item.id}
                                item={item}
                                index={index}
                                onEditar={handleAbrirEditar}
                                onEliminar={handleAbrirDialogEliminar}
                                onInsertarDespues={handleInsertarDespues}
                                mostrarEditar={mostrarEditar}
                            />
                        ))}
                    </Grid>
                </SortableContext>
            </DndContext>

            {/* Modal editar */}
            {mostrarEditar && (
                <ModalCrearEditarItem
                    open={modalEditarOpen}
                    onClose={handleCerrarEditar}
                    idEditar={idEditar}
                    onSuccess={handleSuccess}
                    coleccion={coleccion}
                />
            )}

            {/* Modal insertar debajo */}
            <ModalCrearEditarItem
                open={modalInsertarOpen}
                onClose={() => setModalInsertarOpen(false)}
                onSuccess={handleSuccessInsertar}
                coleccion={coleccion}
            />

            <Dialog open={dialogEliminarOpen} onClose={handleCerrarDialogEliminar}>
                <DialogTitle>¿Eliminar elemento?</DialogTitle>
                <DialogContent>
                    ¿Estás seguro de que deseas eliminar este elemento? Esta acción no se puede deshacer.
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCerrarDialogEliminar}>Cancelar</Button>
                    <Button onClick={handleEliminar} color="error" variant="contained">Eliminar</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
                    {mensajeExito}
                </Alert>
            </Snackbar>
        </>
    );
};

export default Lista_Items_Dashboard;